// ─── the transcript view ─────────────────────────────────────────────────────
// The record of the conversation, and the only place a voice app shows its work.
// Three things it has to get right:
//
//   · attribution — who said this, readable at a glance while you are talking
//   · partial vs final — live text must look provisional, not like something said
//   · scrolling — pinned to the newest turn, but never yanked away from someone
//     reading back, and never a line sliced in half at the top edge
//
// The session broadcasts partials as *deltas* and finals as the whole line (see
// `record()` in the session DO), so the open turn per role is accumulated here and
// replaced when the final arrives.

import type { Role } from '@shared/protocol.ts'

type Kind = Role | 'tool'

const LABEL: Record<Kind, string> = { user: 'you', assistant: 'voice', system: 'session', tool: 'tool' }

/** Beyond this a single turn is folded, so one rambling answer cannot bury the rest. */
const FOLD_CHARS = 620
/** How close to the bottom still counts as "following along". */
const PIN_SLACK_PX = 48

export class TranscriptView {
  private open = new Map<Role, { turn: HTMLElement; body: HTMLElement; text: string }>()
  private pinned = true
  /** Height at the last scroll we credited to the reader — see `settle`. */
  private height = 0

  /**
   * `empty` is shown whenever there is nothing to show — which is where the
   * first-run guide lives, so an untouched app explains itself instead of sitting
   * there blank.
   */
  constructor(
    private readonly root: HTMLElement,
    private readonly empty: HTMLElement = el('div', 'empty', 'Nothing said yet. Press Talk, or type below.'),
  ) {
    this.root.append(this.empty)
    this.height = this.root.clientHeight
    // Reading back scrolls up; new turns must not snatch the view away.
    this.root.addEventListener('scroll', () => this.settle())
    // A box that changes size can move the newest turn out of view without ever firing
    // a scroll event, so the same decision is made on resize too.
    new ResizeObserver(() => this.settle()).observe(this.root)
  }

  /**
   * Is the reader still following the conversation, or reading back? Only a scroll on
   * a box that did NOT just change size answers that. A phone's on-screen keyboard
   * shortens the log mid-relayout, and for one frame it has no overflow at all — so
   * `scrollTop` is 0 through no choice of the reader's, and taking that at face value
   * unpinned the log and left the newest turn off screen at the exact moment they were
   * typing a reply to it.
   */
  private settle(): void {
    if (this.root.clientHeight !== this.height) {
      this.height = this.root.clientHeight
      return this.follow()
    }
    this.pinned = this.root.scrollHeight - this.root.scrollTop - this.root.clientHeight <= PIN_SLACK_PX
    this.root.dataset.pinned = String(this.pinned)
  }

  push(role: Role, text: string, final: boolean): void {
    if (!final) {
      const open = this.open.get(role) ?? this.beginOpen(role)
      open.text += text
      this.fill(open.body, open.text)
      return this.follow()
    }
    const open = this.open.get(role)
    this.open.delete(role)
    if (!text.trim()) return open?.turn.remove()
    if (open) {
      open.turn.classList.remove('partial')
      this.fill(open.body, text)
      return this.follow()
    }
    this.turn(role, text)
  }

  /** Tool activity, shown because a voice UI otherwise hides the interesting half. */
  tool(text: string): void {
    this.turn('tool', text)
  }

  system(text: string): void {
    this.turn('system', text)
  }

  clear(): void {
    this.open.clear()
    this.pinned = true
    this.root.replaceChildren(this.empty)
  }

  /** Plain text of the whole log — what the e2e harness asserts against. */
  get text(): string {
    return this.root.textContent ?? ''
  }

  private beginOpen(role: Role): { turn: HTMLElement; body: HTMLElement; text: string } {
    const turn = this.turn(role, '', true)
    const entry = { turn, body: turn.querySelector<HTMLElement>('.what')!, text: '' }
    this.open.set(role, entry)
    return entry
  }

  private turn(kind: Kind, text: string, partial = false): HTMLElement {
    this.empty.remove()
    const turn = el('div', `turn ${kind}${partial ? ' partial' : ''}`)
    const who = el('div', 'who')
    who.append(el('span', 'badge', LABEL[kind]))
    const body = el('div', 'what')
    // Appended before filling: `fill` hangs the unfold control off the body's parent,
    // and a body that is not in its turn yet has none.
    turn.append(who, body)
    this.fill(body, text)
    this.root.append(turn)
    this.follow()
    return turn
  }

  /**
   * Long turns are folded rather than truncated: the whole text stays in the DOM (it
   * is the transcript), but one answer cannot push every other turn off the screen.
   */
  private fill(body: HTMLElement, text: string): void {
    body.textContent = text
    const long = text.length > FOLD_CHARS
    body.classList.toggle('folded', long && !body.dataset.unfolded)
    const existing = body.parentElement?.querySelector('.unfold')
    if (!long || body.dataset.unfolded) return existing?.remove()
    if (existing) return

    const unfold = el('button', 'unfold', 'show the whole turn')
    unfold.onclick = () => {
      body.dataset.unfolded = 'true'
      body.classList.remove('folded')
      unfold.remove()
    }
    body.parentElement?.append(unfold)
  }

  private follow(): void {
    if (this.pinned) this.root.scrollTop = this.root.scrollHeight
  }
}

function el(tag: string, className: string, text = ''): HTMLElement {
  const node = document.createElement(tag)
  node.className = className
  if (text) node.textContent = text
  return node
}
